import { memo, useEffect, useMemo } from "react";
import { Position } from "../../proto/generated/position";
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { getSecurityKey } from "../../util/security";
import { useSelector } from "react-redux";
import { selectOrdersHistory } from "../../selectors/orders";
import { getAnalyzedGroupedPosition } from "../../util/positions";
import { useStyles } from "./styles";
import AnalyzePositionsTableRow from "./analyzePositionsTableRow";
import { selectBasketId, selectOrderType, selectTradeOrders } from "../../selectors/strategyBuilder";
import { toLocaleStringIn } from "../../util/general";
import { selectOrderBooks, selectSnapshots } from "../../selectors/security";
import LtpCell from "./ltpCell";
import { Broker_OrderType } from "../../proto/generated/order";

export type AnalyzePositionsTableProps = {
    positions: Position[]
    handleProfitChange: (realisedProfit: number, unrealisedProfit: number) => void
}

const AnalyzePositionsTable: React.FC<AnalyzePositionsTableProps> = ({positions, handleProfitChange}) => {

    const { classes, cx } = useStyles();

    const basketId = useSelector(selectBasketId);
    const placedOrders = useSelector(selectOrdersHistory);
    const tradeOrders = useSelector(selectTradeOrders);
    const snapshots = useSelector(selectSnapshots);
    const orderBooks = useSelector(selectOrderBooks);
    const orderType = useSelector(selectOrderType);
    const analyzeGroupedPosition = useMemo(() => {
        return getAnalyzedGroupedPosition(positions as unknown as Position[], basketId, placedOrders, tradeOrders);
    }, [basketId, positions, placedOrders, tradeOrders])

    const profitPair = useMemo(() => {
        return analyzeGroupedPosition?.children.map((position) => {

            if (position.children?.length > 1) {
                return [0, 0];
            }

            const securityKey = getSecurityKey(
                position.security.exchange,
                position.security.code
            );

            const snapshot = snapshots[securityKey];
            const orderBook = orderBooks[securityKey];

            const { squareOffOrderId } = position;
            const squareOffOrder = tradeOrders?.find(
                t => t.clientOrderId === squareOffOrderId
            );

            // If there's a square off order with a price and we're in LIMIT mode, use the fixed price
            // If in MARKET mode, use order book price (bid for selling/squaring off long, ask for buying/squaring off short)
            const squareOffPrice =
                squareOffOrder && squareOffOrder.selected
                    ? (squareOffOrder.price && squareOffOrder.price !== 0 && orderType === Broker_OrderType.LIMIT_ORDER
                        ? squareOffOrder.price
                        : (position.originalPosition.closePosition > 0
                            ? (orderBook?.bids[0]?.price ?? snapshot?.lastTradedPrice ?? 0)
                            : (orderBook?.asks[0]?.price ?? snapshot?.lastTradedPrice ?? 0)))
                    : 0;

            const {
                security,
                avgPrice,
                newQuantity,
                squaredOffQuantity,
            } = position;

            const priceMultiplier = security.priceMultiplier ?? 100;

            // -------------------------
            // REALIZED PROFIT
            // -------------------------
            let realisedProfit = position.realizedProfit || 0;

            if (squaredOffQuantity !== 0 && squareOffPrice) {
                const originalPosition = position.originalPosition;
                if (originalPosition) {
                    const direction = originalPosition.closePosition > 0 ? 1 : -1;
                    const originalAvgPrice =
                        originalPosition.closePositionValue /
                        originalPosition.closePosition;

                    realisedProfit +=
                        direction *
                        squaredOffQuantity *
                        (squareOffPrice - originalAvgPrice);
                }
            }

            realisedProfit = realisedProfit / priceMultiplier;

            // -------------------------
            // UNREALIZED PROFIT
            // -------------------------
            let unrealisedProfit = 0;

            if (newQuantity !== 0 && snapshot) {
                // For incoming positions (addedQuantity !== 0), use LTP for both entry and current price
                // This makes profit = 0 for incoming positions (visual only)
                const isIncoming = (position.addedQuantity ?? 0) !== 0;
                let entryPrice = avgPrice;

                if (isIncoming) {
                    // For incoming positions, use current LTP as entry price so profit = 0
                    entryPrice = snapshot.lastTradedPrice;
                }

                unrealisedProfit =
                    ((snapshot.lastTradedPrice - entryPrice) * newQuantity) /
                    priceMultiplier;
            }

            return [realisedProfit, unrealisedProfit];
        });
    }, [analyzeGroupedPosition, snapshots, tradeOrders, orderType, orderBooks]);

    const realisedProfit = useMemo(() => (profitPair || []).reduce((sum, i) => sum + i[0], 0), [profitPair])
    const unrealisedProfit = useMemo(() => (profitPair || []).reduce((sum, i) => sum + i[1], 0), [profitPair])

    useEffect(() => {
        handleProfitChange(realisedProfit, unrealisedProfit)
    }, [realisedProfit, unrealisedProfit, handleProfitChange])

    const tableHeader = useMemo(() => (
        <TableRow >
            <TableCell className={cx(classes.tableCell, classes.symbolCell)}>
                <Typography variant="caption" color="textSecondary">SYMBOL</Typography>
            </TableCell>
            <TableCell className={cx(classes.tableCell, classes.qtyCell)}>
                <Typography variant="caption" color="textSecondary">QTY</Typography>
            </TableCell>
            <TableCell className={cx(classes.tableCell, classes.averageCell)}>
                <Typography variant="caption" color="textSecondary">AVG</Typography>
            </TableCell>
            <TableCell className={cx(classes.tableCell, classes.ltpCell)}>
                <Typography variant="caption" color="textSecondary">LTP</Typography>
            </TableCell>
            <TableCell className={cx(classes.tableCell, classes.profitCell)} sx={{ position: 'relative' }}>
                <Typography variant="caption" color="textSecondary">PRFT</Typography>
            </TableCell>
        </TableRow>), [])

    const quantityDisplay = useMemo(() => {
        const currentQty = analyzeGroupedPosition?.newQuantity ?? analyzeGroupedPosition?.quantity ?? 0;

        if (currentQty === 0 && analyzeGroupedPosition?.children?.length) {
            const allPositions = analyzeGroupedPosition.children;
            const originalQuantities = allPositions.map(p => Math.abs(p.quantity)).sort();
            const originalQty = allPositions.length > 1
                ? (originalQuantities.length > 0 ? originalQuantities.reduce((a, b) => {
                    const gcd = (x: number, y: number): number => y === 0 ? x : gcd(y, x % y);
                    return gcd(a, b);
                  }) : 0)
                : (originalQuantities[0] || 0);
            const wasPositive = allPositions[0]?.quantity > 0;

            return <>
                <span className={cx(
                    classes.oldQtyDisplay,
                    wasPositive ? classes.buyQuantity : classes.sellQuantity
                )}>{wasPositive ? originalQty : -originalQty}</span>
                <span>{0}</span>
            </>;
        }

        return <span className={cx(
            currentQty > 0 ? classes.buyQuantity : undefined,
            currentQty < 0 ? classes.sellQuantity : undefined
        )}>{currentQty}</span>
    }, [analyzeGroupedPosition, classes])

    return <Table className={classes.table}>
            <TableHead>
                {tableHeader}
            </TableHead>
            <TableBody>
                <TableRow>
                    <TableCell className={cx(classes.tableCell, classes.symbolCell)}>
                        {analyzeGroupedPosition?.groupName}
                    </TableCell>
                    <TableCell className={cx(classes.tableCell, classes.qtyCell)}>
                        <Typography variant="caption">
                            {quantityDisplay}
                        </Typography>
                    </TableCell>
                    <TableCell className={cx(classes.tableCell, classes.averageCell)}>
                        {analyzeGroupedPosition.avgPrice ? toLocaleStringIn(analyzeGroupedPosition?.avgPrice/100) : '-'}
                    </TableCell>
                    <TableCell className={cx(classes.tableCell, classes.ltpCell)}>
                        {analyzeGroupedPosition ? <LtpCell position={analyzeGroupedPosition} /> : '-'}
                    </TableCell>
                    <TableCell className={cx(classes.tableCell, classes.profitCell)} sx={{ verticalAlign: 'middle', padding: '5px', position: 'relative' }}>
                        <Typography 
                            variant="body2" 
                            className={cx(
                                unrealisedProfit > 0 ? classes.profitColor : undefined, 
                                unrealisedProfit < 0 ? classes.lossColor : undefined
                            )}
                            style={{ textAlign: 'right' }}
                        >
                            {toLocaleStringIn(unrealisedProfit ?? 0)}
                        </Typography>
                    </TableCell>
                </TableRow>
                {
                    analyzeGroupedPosition ? analyzeGroupedPosition.children.map((position, i) => (
                        <AnalyzePositionsTableRow key={`${position.groupId}-${getSecurityKey(position.security.exchange, position.security.code)}-${i}`} position={position} />
                    )) : null
                }
            </TableBody>
    </Table>;
}


export default memo(AnalyzePositionsTable)